<template>
  <b-container fluid class="settings-root">
    <b-row>
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-collapse is-nav>
          <b-navbar-nav>
            <b-nav-item :active="activeTab === 'theme'" @click="activeTab = 'theme'">
              theme
            </b-nav-item>
            <b-nav-item :active="activeTab === 'dates'" @click="activeTab = 'dates'">
              dates
            </b-nav-item>
            <b-nav-item :active="activeTab === 'users'" @click="activeTab = 'users'">
              users
            </b-nav-item>
            <b-nav-item :active="activeTab === 'oauth'" @click="activeTab = 'oauth'">
              oauth providers
            </b-nav-item>
            <b-nav-item :active="activeTab === 'oauth-server'" @click="activeTab = 'oauth-server'">
              oauth server
            </b-nav-item>
            <b-nav-item :active="activeTab === 'api'" @click="activeTab = 'api'">
              api
            </b-nav-item>
            <b-nav-item :active="activeTab === 'mail'" @click="activeTab = 'mail'">
              email
            </b-nav-item>
            <b-nav-item :active="activeTab === 'embed'" @click="activeTab = 'embed'">
              embed
            </b-nav-item>
            <b-nav-item :active="activeTab === 'code'" @click="activeTab = 'code'">
              custom code
            </b-nav-item>
            <li class="nav-item d-flex align-items-center">
              <modal-help :topic="settingsHelpTopic" label="Help for this screen" show-label variant="link" />
            </li>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </b-row>

    <!-- Theme tab -->
    <b-row v-if="activeTab === 'theme'" class="settings-panels">
      <b-col cols="3" class="theme-list-col">
        <div class="theme-list-header">
          <div class="settings-section-title">Theme</div>
          <div class="settings-section-desc">Select a theme to preview, then save.</div>
          <button class="upload-btn" @click="triggerFileUpload" :disabled="uploading">
            <font-awesome-icon icon="upload" />
            <template v-if="uploading">uploading...</template>
            <template v-else>upload custom theme</template>
          </button>
          <input
            ref="fileInput"
            type="file"
            accept=".css"
            style="display: none"
            @change="handleFileUpload"
          />
        </div>
        <div class="theme-list">
          <div
            v-for="theme in allThemes"
            :key="theme.id"
            class="theme-card"
            :class="{
              'theme-card-previewing': previewTheme === theme.id,
              'theme-card-saved': savedTheme === theme.id && previewTheme !== theme.id
            }"
            @click="previewTheme = theme.id"
          >
            <div class="theme-card-name">
              {{ theme.name }}
              <span v-if="theme.isCustom" class="theme-card-custom-badge">custom</span>
            </div>
            <div class="theme-card-actions">
              <div class="theme-card-badge" v-if="savedTheme === theme.id && previewTheme !== theme.id">current</div>
              <font-awesome-icon icon="check" class="theme-card-check" v-if="previewTheme === theme.id" />
              <button
                v-if="theme.isCustom"
                class="theme-delete-btn"
                @click.stop="deleteCustomTheme(theme.id)"
                title="Delete custom theme"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>
          <button
            class="save-btn"
            :class="{ 'save-btn-disabled': !hasChanges }"
            :disabled="!hasChanges || saving"
            @click="saveTheme"
          >
            <template v-if="saving">saving...</template>
            <template v-else-if="saved">saved!</template>
            <template v-else>save theme</template>
          </button>
        </div>
        <div class="theme-list-footer">
        </div>
      </b-col>

      <b-col cols="9" class="preview-col">
        <div class="preview-header">
          <span class="preview-label">Preview: {{ previewThemeName }}</span>
          <span v-if="hasChanges" class="preview-unsaved">unsaved</span>
        </div>
        <div class="preview-frame-wrapper">
          <iframe
            class="preview-frame"
            :srcdoc="previewHtml"
            sandbox="allow-same-origin"
          ></iframe>
        </div>
      </b-col>
    </b-row>

    <b-row v-if="activeTab === 'dates'" class="settings-panels">
      <b-col cols="12" md="7" class="dates-panel">
        <div class="settings-section-title">Time zone</div>
        <div class="settings-section-desc">
          Dates are stored in UTC. This time zone is used when a date is entered and when it is shown, including on the calendar.
        </div>
        <b-form-select v-model="timeZone" :options="timeZoneOptions" class="dates-zone-select" />
        <p v-if="timeZoneError" class="dates-error">{{ timeZoneError }}</p>
        <button class="save-btn" :disabled="savingTimeZone || timeZone === savedTimeZone" @click="saveTimeZone">
          <template v-if="savingTimeZone">saving...</template>
          <template v-else-if="timeZoneSaved">saved!</template>
          <template v-else>save time zone</template>
        </button>
      </b-col>
    </b-row>

    <b-row v-if="activeTab === 'users'" class="settings-panels">
      <b-col cols="12" md="7" class="dates-panel">
        <div class="settings-section-title">Sign-in session</div>
        <div class="settings-section-desc">
          These limits apply to people who sign in on the live app. Session length is how long a sign-in lasts, even while they are still using the app. Inactivity signs them out when they stop using the mouse, keyboard, or scroll wheel.
        </div>
        <label class="session-label" for="session-count">Session length</label>
        <div class="session-row">
          <b-form-input id="session-count" type="number" min="1" v-model.number="sessionCount" class="session-count" />
          <b-form-select v-model="sessionUnit" :options="sessionLengthUnits" class="session-unit" />
        </div>
        <label class="mail-secure">
          <input type="checkbox" v-model="inactivityEnabled" />
          Sign out after inactivity
        </label>
        <div v-if="inactivityEnabled" class="session-row">
          <b-form-input id="inactivity-count" type="number" min="1" v-model.number="inactivityCount" class="session-count" aria-label="Inactivity timeout" />
          <b-form-select v-model="inactivityUnit" :options="inactivityUnits" class="session-unit" aria-label="Inactivity unit" />
        </div>
        <label class="session-label" for="remember-count">Remember me</label>
        <div class="session-row">
          <b-form-input id="remember-count" type="number" min="1" v-model.number="rememberCount" class="session-count" :disabled="inactivityEnabled" aria-label="Remember me length" />
          <b-form-select v-model="rememberUnit" :options="sessionLengthUnits" class="session-unit" :disabled="inactivityEnabled" aria-label="Remember me unit" />
        </div>
        <p class="settings-section-desc session-note">
          A shorter session length ends open sign-ins that have already passed it. A longer one applies the next time someone signs in. Inactivity applies to sign-ins that are already open. Leaving inactivity off keeps the previous behavior: the sign-in lasts until the session length. The starting value is 30 days. Remember me keeps a longer sign-in when the person checks it. It is ignored while inactivity sign-out is on. With inactivity on, the live app warns one minute before the sign-in ends, and the sign-in still ends if the person does nothing.
        </p>
        <p v-if="sessionError" class="dates-error">{{ sessionError }}</p>
        <button class="save-btn session-save" :disabled="savingSession" @click="saveUserSession">
          <template v-if="savingSession">saving...</template>
          <template v-else-if="sessionSaved">saved!</template>
          <template v-else>save sign-in session</template>
        </button>

        <div class="settings-section-title policy-title">Authenticator</div>
        <div class="settings-section-desc">
          This applies to every person who signs in on the live app. Optional keeps the checkbox on a login or registration view. Required asks even when that checkbox is off. Off does not ask.
        </div>
        <label class="policy-choice" for="authenticator-off">
          <input id="authenticator-off" type="radio" value="off" v-model="authenticatorPolicy" />
          <span>Off</span>
        </label>
        <label class="policy-choice" for="authenticator-optional">
          <input id="authenticator-optional" type="radio" value="optional" v-model="authenticatorPolicy" />
          <span>Optional</span>
        </label>
        <label class="policy-choice" for="authenticator-required">
          <input id="authenticator-required" type="radio" value="required" v-model="authenticatorPolicy" />
          <span>Required</span>
        </label>
        <p class="settings-section-desc session-note">
          Required asks a person who has not enrolled to set one up, including on a login view that does not have the checkbox. Someone who already enrolled is asked for a code on later sign-ins unless the policy is off. Clearing one person’s authenticator is on their row in the login spreadsheet.
        </p>
        <p v-if="policyError" class="dates-error">{{ policyError }}</p>
        <button class="save-btn session-save" :disabled="savingPolicy || authenticatorPolicy === savedAuthenticatorPolicy" @click="saveAuthenticatorPolicy">
          <template v-if="savingPolicy">saving...</template>
          <template v-else-if="policySaved">saved!</template>
          <template v-else>save authenticator policy</template>
        </button>

        <div class="settings-section-title policy-title">Passwords</div>
        <div class="settings-section-desc">
          These checks run wherever a password is saved: registration, a password change, and an edit of a password column. Leave a box empty to leave that rule off.
        </div>
        <label class="session-label" for="password-min-length">Minimum length</label>
        <b-form-input id="password-min-length" type="number" min="1" v-model="minLength" class="session-count" placeholder="off" />
        <label class="mail-secure">
          <input type="checkbox" v-model="blockCommon" />
          Block common passwords
        </label>
        <label class="mail-secure">
          <input type="checkbox" v-model="requireNumber" />
          Require a number
        </label>
        <label class="mail-secure">
          <input type="checkbox" v-model="requireSymbol" />
          Require a symbol
        </label>
        <label class="mail-secure">
          <input type="checkbox" v-model="requireMixedCase" />
          Require uppercase and lowercase letters
        </label>
        <label class="session-label" for="password-expiry">Expiry in days</label>
        <b-form-input id="password-expiry" type="number" min="1" v-model="expiryDays" class="session-count" placeholder="off" />
        <label class="mail-secure">
          <input type="checkbox" v-model="preventReuse" />
          Do not reuse the last three passwords
        </label>
        <label class="session-label" for="lockout-minutes">Lockout minutes</label>
        <b-form-input id="lockout-minutes" type="number" min="1" v-model.number="lockoutMinutes" class="session-count" />
        <label class="session-label" for="lockout-message">Lockout message</label>
        <b-form-textarea id="lockout-message" v-model="lockoutMessage" rows="2" max-rows="4" />
        <label class="mail-secure">
          <input type="checkbox" v-model="clearAuthenticatorOnReset" />
          Clear the authenticator when a password is reset
        </label>
        <p class="settings-section-desc session-note">
          Five wrong passwords in 15 minutes lock that name for the duration above. The person receives the message by email when the app has an email account and the name is an email address. A reset link sets a new password and clears the lock. It leaves the authenticator in place unless the box above is on.
        </p>
        <p v-if="passwordError" class="dates-error">{{ passwordError }}</p>
        <button class="save-btn session-save" :disabled="savingPasswords" @click="savePasswordPolicy">
          <template v-if="savingPasswords">saving...</template>
          <template v-else-if="passwordsSaved">saved!</template>
          <template v-else>save password rules</template>
        </button>
      </b-col>
    </b-row>

    <!-- OAuth Providers tab -->
    <SettingsOAuth v-if="activeTab === 'oauth'" :application="application" />

    <!-- OAuth Server tab -->
    <SettingsOAuthServer v-if="activeTab === 'oauth-server'" :application="application" />

    <!-- API tab -->
    <SettingsApi v-if="activeTab === 'api'" :application="application" />

    <b-row v-if="activeTab === 'embed'" class="settings-panels">
      <b-col cols="12" md="8" class="dates-panel">
        <div class="settings-section-title">Embed</div>
        <p class="settings-section-desc">Choose the whole app, one page, or one view. A page embed leaves out the menu. A link or a redirect to another page in this app still opens that page.</p>
        <EmbedChooser :application="application" start-mode="app" />

        <div class="embed-hosts">
          <div class="settings-section-title">External hosts</div>
          <p class="settings-section-desc">One list for the whole app. A presentation rule can send a visitor to an address on one of these hosts.</p>
          <label class="session-label" for="redirect-hosts">Hosts a redirect may use</label>
          <b-form-textarea id="redirect-hosts" v-model="redirectHostsText" rows="4" max-rows="8" placeholder="help.example.com" />
          <p v-if="hostsFromPages" class="settings-section-desc session-note">These hosts are still stored on individual pages. Save them here so every page uses this list.</p>
          <p v-else class="settings-section-desc session-note">One host per line. An address on any other host stays in the app. A link or a redirect to another page does not use this list.</p>
          <p v-if="hostsError" class="dates-error">{{ hostsError }}</p>
          <button type="button" class="save-btn session-save" :disabled="savingHosts" @click="saveRedirectHosts">
            <template v-if="savingHosts">saving...</template>
            <template v-else-if="hostsSaved">saved!</template>
            <template v-else>save hosts</template>
          </button>
        </div>
      </b-col>
    </b-row>

    <SettingsCustomCode v-show="activeTab === 'code'" :application="application" />

    <b-row v-if="activeTab === 'mail'" class="settings-panels">
      <b-col cols="12" class="mail-panel">
        <div class="mail-card">
          <header class="mail-head">
            <div class="mail-head-mark" aria-hidden="true">
              <font-awesome-icon icon="envelope" />
            </div>
            <div class="mail-head-copy">
              <div class="settings-section-title">Email</div>
              <p class="settings-section-desc mail-lead">
                Each account is one mailbox. A Send Email automation chooses which account sends.
              </p>
            </div>
            <span class="mail-pill" :class="mailReady ? 'mail-pill-ready' : 'mail-pill-empty'">
              {{ mailReady ? 'ready' : 'not configured' }}
            </span>
          </header>

          <div v-if="!mail.accounts.length" class="mail-empty">
            <p>Add an account for each mailbox you send from.</p>
            <button type="button" class="mail-add" @click="addMailAccount">add email account</button>
          </div>

          <article v-for="account in mail.accounts" :key="account.id" class="mail-account">
            <div class="mail-account-head">
              <label class="mail-field mail-field-name">
                <span class="mail-label">Name</span>
                <b-form-input v-model="account.name" placeholder="account name" autocomplete="off" />
              </label>
              <button
                v-if="account.id === mail.defaultAccountId"
                type="button"
                class="mail-default"
                disabled
              >default</button>
              <button
                v-else
                type="button"
                class="mail-default-set"
                @click="makeDefaultMailAccount(account)"
              >make default</button>
              <button type="button" class="mail-remove" aria-label="Remove email account" @click="removeMailAccount(account)">
                remove
              </button>
            </div>

            <div class="mail-modes" role="radiogroup" aria-label="Connection">
              <button
                v-for="mode in mailModes"
                :key="mode.value"
                type="button"
                class="mail-mode"
                :class="{ 'is-on': account.mode === mode.value }"
                :aria-pressed="account.mode === mode.value ? 'true' : 'false'"
                @click="setMailMode(account, mode.value)"
              >
                <span class="mail-mode-label">{{ mode.label }}</span>
                <span class="mail-mode-detail">{{ mode.detail }}</span>
              </button>
            </div>

            <div class="mail-pair mail-pair-server">
              <label class="mail-field">
                <span class="mail-label">Host</span>
                <b-form-input v-model="account.host" placeholder="smtp host" autocomplete="off" />
              </label>
              <label class="mail-field">
                <span class="mail-label">Port</span>
                <b-form-input v-model="account.port" placeholder="port" inputmode="numeric" />
              </label>
            </div>

            <div class="mail-pair mail-pair-account">
              <label class="mail-field">
                <span class="mail-label">Username</span>
                <b-form-input v-model="account.user" placeholder="username" autocomplete="off" />
              </label>
              <label class="mail-field">
                <span class="mail-label">Password</span>
                <b-form-input v-model="account.password" type="password" placeholder="password" autocomplete="new-password" />
              </label>
            </div>
            <p class="mail-note">Leave the password blank to keep the saved one.</p>

            <label class="mail-field">
              <span class="mail-label">From address</span>
              <b-form-input v-model="account.from" placeholder="from address" autocomplete="off" />
            </label>
          </article>

          <div v-if="mail.accounts.length" class="mail-add-row">
            <button type="button" class="mail-add" @click="addMailAccount">add email account</button>
          </div>

          <footer class="mail-actions">
            <p v-if="mailError" class="dates-error mail-error">{{ mailError }}</p>
            <span v-else class="mail-actions-note">The default account is used when an automation does not pick one.</span>
            <button type="button" class="mail-save" :disabled="savingMail" @click="saveMail">
              <template v-if="savingMail">saving...</template>
              <template v-else-if="mailSaved">saved!</template>
              <template v-else>save email</template>
            </button>
          </footer>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>

import axios from 'axios'
import themes from '../../lib/themes.js'
import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'
import { timeZoneChoices, timeZoneLabel, normalizeTimeZone } from '../../../../shared/app-timezone.js'
import { minutesToParts, partsToMinutes, normalizeUserSession, userSessionForRead, displayedRememberMinutes } from '../../../../shared/user-session-policy.js'
import { authenticatorPolicyForRead } from '../../../../shared/app-two-factor.js'
import { passwordPolicyForRead, DEFAULT_LOCKOUT_MESSAGE } from '../../../../shared/password-policy.js'
import { redirectHostAllowList, redirectHostsFromPages } from '../../../../shared/page-open-rules.js'
import { themeCssPath } from '../../../../shared/themes.js'
import { MAIL_CONNECTION_MODES, blankMailAccount, mailPortForMode, normalizeMailSettings } from '../../../../shared/automation-integration.js'
import SettingsOAuth from './settings_oauth.vue'
import SettingsOAuthServer from './settings_oauth_server.vue'
import SettingsApi from './settings_api.vue'
import SettingsCustomCode from './settings_custom_code.vue'
import EmbedChooser from '../EmbedChooser.vue'

const DEFAULT_THEME_CSS = themeCssPath('default')

const PREVIEW_BODY_HTML = `
<!-- Navbar -->
<nav class="navbar navbar-expand-md navbar-dark bg-primary">
  <div class="container-fluid">
    <a class="navbar-brand" href="#">MyApp</a>
    <button class="navbar-toggler" type="button"><span class="navbar-toggler-icon"></span></button>
    <div class="collapse navbar-collapse show">
      <ul class="navbar-nav me-auto">
        <li class="nav-item"><a class="nav-link active" href="#">Home</a></li>
        <li class="nav-item"><a class="nav-link" href="#">Features</a></li>
        <li class="nav-item dropdown">
          <a class="nav-link dropdown-toggle" href="#" role="button">More</a>
        </li>
      </ul>
      <form class="d-flex" role="search">
        <input class="form-control me-2" type="search" placeholder="Search">
        <button class="btn btn-outline-light" type="button">Go</button>
      </form>
    </div>
  </div>
</nav>

<div class="preview-body">

<!-- Alerts -->
<section>
  <h6>Alerts</h6>
  <div class="alert alert-primary" role="alert">This is a <strong>primary</strong> alert.</div>
  <div class="alert alert-success" role="alert">This is a <strong>success</strong> alert.</div>
  <div class="alert alert-warning" role="alert">This is a <strong>warning</strong> alert.</div>
  <div class="alert alert-danger" role="alert">This is a <strong>danger</strong> alert.</div>
</section>

<!-- Buttons & Badges -->
<section>
  <h6>Buttons</h6>
  <div class="d-flex flex-wrap gap-2 mb-3">
    <button class="btn btn-primary">Primary</button>
    <button class="btn btn-secondary">Secondary</button>
    <button class="btn btn-success">Success</button>
    <button class="btn btn-danger">Danger</button>
    <button class="btn btn-warning">Warning</button>
    <button class="btn btn-info">Info</button>
    <button class="btn btn-outline-primary">Outline</button>
    <button class="btn btn-outline-secondary">Outline</button>
  </div>
  <div class="d-flex flex-wrap gap-2">
    <span class="badge bg-primary">Primary</span>
    <span class="badge bg-secondary">Secondary</span>
    <span class="badge bg-success">Success</span>
    <span class="badge bg-danger">Danger</span>
    <span class="badge bg-warning text-dark">Warning</span>
    <span class="badge bg-info text-dark">Info</span>
  </div>
</section>

<!-- Table -->
<section>
  <h6>Table</h6>
  <table class="table table-striped table-hover">
    <thead>
      <tr><th>#</th><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr>
    </thead>
    <tbody>
      <tr><td>1</td><td>Alice Johnson</td><td>alice@example.com</td><td>Admin</td><td><span class="badge bg-success">Active</span></td></tr>
      <tr><td>2</td><td>Bob Smith</td><td>bob@example.com</td><td>Editor</td><td><span class="badge bg-success">Active</span></td></tr>
      <tr><td>3</td><td>Carol Davis</td><td>carol@example.com</td><td>Viewer</td><td><span class="badge bg-warning text-dark">Pending</span></td></tr>
      <tr><td>4</td><td>Dan Wilson</td><td>dan@example.com</td><td>Editor</td><td><span class="badge bg-danger">Inactive</span></td></tr>
      <tr><td>5</td><td>Eve Martinez</td><td>eve@example.com</td><td>Admin</td><td><span class="badge bg-success">Active</span></td></tr>
    </tbody>
  </table>
</section>

<!-- Cards -->
<section>
  <h6>Cards</h6>
  <div class="row g-3">
    <div class="col-md-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">Dashboard</h5>
          <p class="card-text">View analytics and reporting data for your organization.</p>
          <a href="#" class="btn btn-primary btn-sm">Open</a>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">Settings</h5>
          <p class="card-text">Manage your account preferences and configurations.</p>
          <a href="#" class="btn btn-secondary btn-sm">Configure</a>
        </div>
      </div>
    </div>
    <div class="col-md-4">
      <div class="card">
        <div class="card-body">
          <h5 class="card-title">Notifications</h5>
          <p class="card-text">You have <span class="badge bg-danger">3</span> unread notifications.</p>
          <a href="#" class="btn btn-outline-primary btn-sm">View all</a>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Form -->
<section>
  <h6>Form</h6>
  <form>
    <div class="row g-3">
      <div class="col-md-6">
        <label class="form-label">Full Name</label>
        <input type="text" class="form-control" placeholder="Jane Doe" value="Jane Doe">
      </div>
      <div class="col-md-6">
        <label class="form-label">Email</label>
        <input type="email" class="form-control" placeholder="jane@example.com" value="jane@example.com">
      </div>
      <div class="col-md-6">
        <label class="form-label">Role</label>
        <select class="form-select">
          <option selected>Admin</option>
          <option>Editor</option>
          <option>Viewer</option>
        </select>
      </div>
      <div class="col-md-6">
        <label class="form-label">Department</label>
        <input type="text" class="form-control" placeholder="Engineering" value="Engineering">
      </div>
      <div class="col-12">
        <label class="form-label">Bio</label>
        <textarea class="form-control" rows="2" placeholder="A short bio...">Full-stack developer with 5 years of experience.</textarea>
      </div>
      <div class="col-12">
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="checkbox" checked>
          <label class="form-check-label">Receive email updates</label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="checkbox">
          <label class="form-check-label">Public profile</label>
        </div>
      </div>
      <div class="col-12">
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="status" checked>
          <label class="form-check-label">Active</label>
        </div>
        <div class="form-check form-check-inline">
          <input class="form-check-input" type="radio" name="status">
          <label class="form-check-label">Inactive</label>
        </div>
      </div>
    </div>
    <div class="mt-3">
      <button type="button" class="btn btn-primary">Save changes</button>
      <button type="button" class="btn btn-outline-secondary ms-2">Cancel</button>
    </div>
  </form>
</section>

<!-- Progress -->
<section>
  <h6>Progress Bars</h6>
  <div class="progress mb-2" style="height: 20px;"><div class="progress-bar" style="width: 75%">75%</div></div>
  <div class="progress mb-2" style="height: 20px;"><div class="progress-bar bg-success" style="width: 50%">50%</div></div>
  <div class="progress mb-2" style="height: 20px;"><div class="progress-bar bg-warning" style="width: 33%">33%</div></div>
  <div class="progress" style="height: 20px;"><div class="progress-bar bg-danger" style="width: 15%">15%</div></div>
</section>

<!-- List group -->
<section>
  <h6>List Group</h6>
  <ul class="list-group">
    <li class="list-group-item d-flex justify-content-between align-items-center">Inbox <span class="badge bg-primary rounded-pill">14</span></li>
    <li class="list-group-item d-flex justify-content-between align-items-center">Drafts <span class="badge bg-secondary rounded-pill">2</span></li>
    <li class="list-group-item d-flex justify-content-between align-items-center active">Sent <span class="badge bg-light text-dark rounded-pill">99+</span></li>
    <li class="list-group-item d-flex justify-content-between align-items-center">Trash <span class="badge bg-danger rounded-pill">5</span></li>
  </ul>
</section>

</div>`

const PREVIEW_EXTRA_STYLES = `
  body { padding: 0; margin: 0; }
  .preview-body { padding: 16px 20px; }
  section { margin-bottom: 24px; }
  section > h6 { text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.06em; opacity: 0.5; margin-bottom: 8px; }`

function buildPreviewHtml (cssUrl) {

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<link rel="stylesheet" href="${cssUrl}">
<style>${PREVIEW_EXTRA_STYLES}</style>
</head>
<body>
${PREVIEW_BODY_HTML}
</body>
</html>`
}

function buildPreviewHtmlInline (cssContent) {

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>${cssContent}</style>
<style>${PREVIEW_EXTRA_STYLES}</style>
</head>
<body>
${PREVIEW_BODY_HTML}
</body>
</html>`
}

export default {
  name: 'AppSettings',
  components: { SettingsOAuth, SettingsOAuthServer, SettingsApi, SettingsCustomCode, EmbedChooser },
  data () {
    return {
      activeTab: 'theme',
      themes,
      customThemes: [],
      customCssCache: {},
      previewTheme: 'default',
      savedTheme: 'default',
      saving: false,
      saved: false,
      uploading: false,
      timeZone: 'UTC',
      savedTimeZone: 'UTC',
      savingTimeZone: false,
      timeZoneSaved: false,
      timeZoneError: '',
      mail: { accounts: [], defaultAccountId: '' },
      mailModes: MAIL_CONNECTION_MODES,
      savingMail: false,
      mailSaved: false,
      mailError: '',
      sessionCount: 30,
      sessionUnit: 'days',
      inactivityEnabled: false,
      inactivityCount: 15,
      inactivityUnit: 'minutes',
      rememberCount: 14,
      rememberUnit: 'days',
      savingSession: false,
      sessionSaved: false,
      sessionError: '',
      minLength: '',
      blockCommon: false,
      requireNumber: false,
      requireSymbol: false,
      requireMixedCase: false,
      expiryDays: '',
      preventReuse: false,
      lockoutMinutes: 15,
      lockoutMessage: DEFAULT_LOCKOUT_MESSAGE,
      clearAuthenticatorOnReset: false,
      savingPasswords: false,
      passwordsSaved: false,
      passwordError: '',
      redirectHostsText: '',
      hostsFromPages: false,
      savingHosts: false,
      hostsSaved: false,
      hostsError: '',
      authenticatorPolicy: 'optional',
      savedAuthenticatorPolicy: 'optional',
      savingPolicy: false,
      policySaved: false,
      policyError: '',
      sessionLengthUnits: [
        { value: 'minutes', text: 'minutes' },
        { value: 'hours', text: 'hours' },
        { value: 'days', text: 'days' }
      ],
      inactivityUnits: [
        { value: 'minutes', text: 'minutes' },
        { value: 'hours', text: 'hours' },
        { value: 'days', text: 'days' }
      ]
    }
  },
  computed: {
    application () {
      return this.$route.params.application
    },
    settingsHelpTopic () {

      return {
        theme: 'settings-theme',
        dates: 'settings-dates',
        oauth: 'settings-oauth',
        'oauth-server': 'settings-oauth-server',
        api: 'settings-api',
        mail: 'settings-mail',
        embed: 'settings-embed',
        code: 'settings-code',
        users: 'settings-users'
      }[this.activeTab] || 'settings-theme'
    },
    timeZoneOptions () {
      return timeZoneChoices().map(zone => ({ value: zone, text: timeZoneLabel(zone) }))
    },
    allThemes () {

      const builtIn = this.themes.map(t => ({
        id: t.id,
        name: t.name,
        css: t.css,
        isCustom: false
      }))

      const custom = this.customThemes.map(t => ({
        id: t.themeId,
        name: t.name,
        css: null,
        isCustom: true
      }))

      return [...builtIn, ...custom]
    },
    hasChanges () {
      return this.previewTheme !== this.savedTheme
    },
    mailReady () {
      const account = this.mail.accounts.find(item => item.id === this.mail.defaultAccountId) || this.mail.accounts[0]
      if (!account) return false
      return !!(String(account.host || '').trim() && String(account.from || '').trim())
    },
    previewThemeName () {

      const t = this.allThemes.find(t => t.id === this.previewTheme)
      return t ? t.name : 'Default Bootstrap'
    },
    previewCssUrl () {

      const t = this.allThemes.find(t => t.id === this.previewTheme)

      if (!t) return DEFAULT_THEME_CSS
      if (t.isCustom) return null
      return t.css || DEFAULT_THEME_CSS
    },
    previewHtml () {

      const t = this.allThemes.find(t => t.id === this.previewTheme)

      if (t && t.isCustom && this.customCssCache[t.id]) {

        return buildPreviewHtmlInline(this.customCssCache[t.id])
      }

      return buildPreviewHtml(this.previewCssUrl || DEFAULT_THEME_CSS)
    }
  },
  async mounted () {

    if (this.$route.query.tab === 'mail') this.activeTab = 'mail'
    if (this.$route.query.tab === 'code') this.activeTab = 'code'

    await this.loadSettings()
    await this.loadCustomThemes()
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {

    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  watch: {
    async '$route.params.application' () {

      await this.loadSettings()
      await this.loadCustomThemes()
    },
    async previewTheme (newId) {

      const t = this.allThemes.find(t => t.id === newId)

      if (t && t.isCustom && !this.customCssCache[newId]) {

        try {

          const res = await axios.get(
            `${VUE_APP_PROXY_SERVER_URL}/custom-themes/${this.application}/${newId}/style.css`,
            { responseType: 'text', transformResponse: [data => data] }
          )

          this.customCssCache[newId] = res.data
        } catch (err) {

          console.log('failed to fetch custom theme css for preview', err)
        }
      }
    }
  },
  methods: {
    usersSessionSnap () {

      return JSON.stringify({
        sessionCount: this.sessionCount,
        sessionUnit: this.sessionUnit,
        inactivityEnabled: this.inactivityEnabled,
        inactivityCount: this.inactivityCount,
        inactivityUnit: this.inactivityUnit,
        rememberCount: this.rememberCount,
        rememberUnit: this.rememberUnit
      })
    },
    passwordSnap () {

      return JSON.stringify({
        minLength: this.minLength,
        blockCommon: this.blockCommon,
        requireNumber: this.requireNumber,
        requireSymbol: this.requireSymbol,
        requireMixedCase: this.requireMixedCase,
        expiryDays: this.expiryDays,
        preventReuse: this.preventReuse,
        lockoutMinutes: this.lockoutMinutes,
        lockoutMessage: this.lockoutMessage,
        clearAuthenticatorOnReset: this.clearAuthenticatorOnReset
      })
    },
    usersSectionDirty () {

      const sessionDirty = this._sessionSnap != null && this._sessionSnap !== this.usersSessionSnap()
      const passwordDirty = this._passwordSnap != null && this._passwordSnap !== this.passwordSnap()
      const policyDirty = this.savedAuthenticatorPolicy != null && this.authenticatorPolicy !== this.savedAuthenticatorPolicy

      return sessionDirty || passwordDirty || policyDirty
    },
    mailSectionDirty () {

      return this._mailSnap != null && JSON.stringify(this.mail) !== this._mailSnap
    },
    hostsSectionDirty () {

      return this._hostsReady === true && this.redirectHostsText !== this._hostsSnap
    },
    rememberHosts () {

      this._hostsSnap = this.redirectHostsText
      this._hostsReady = true
    },
    applyThemeFromServer (data) {

      if (data && data.theme) {

        this.savedTheme = data.theme
        this.previewTheme = data.theme
      }

      return this.loadCustomThemes()
    },
    applyTimeZoneFromServer (data) {

      const zone = normalizeTimeZone(data && data.timeZone)

      this.timeZone = zone
      this.savedTimeZone = zone
    },
    applyUsersFromServer (data) {

      this.applyUserSession(data && data.userSession)
      this.applyAuthenticatorPolicy(data && data.authenticatorPolicy)
      this.applyPasswordPolicy(data && data.passwordPolicy)
    },
    async applyDesignerChange (change) {

      const keys = change && change.keys || []
      const appWide = keys.includes('application') || keys.includes('settings')
      const sections = [
        { key: 'settings:appearance', dirty: () => this.hasChanges, label: 'appearance', apply: (data) => this.applyThemeFromServer(data) },
        { key: 'settings:dates', dirty: () => this.timeZone !== this.savedTimeZone, label: 'the time zone', apply: (data) => this.applyTimeZoneFromServer(data) },
        { key: 'settings:users', dirty: () => this.usersSectionDirty(), label: 'user settings', apply: (data) => this.applyUsersFromServer(data) },
        { key: 'settings:mail', dirty: () => this.mailSectionDirty(), label: 'mail settings', apply: (data) => this.applyMail(data && data.mail) },
        { key: 'settings:embed', dirty: () => this.hostsSectionDirty(), label: 'embed settings', apply: (data) => this.applyRedirectHosts(data) },
        { key: 'custom-themes', dirty: () => false, label: 'themes', apply: () => this.loadCustomThemes() }
      ]
      const relevant = sections.filter((section) => appWide || keys.includes(section.key))

      if (!relevant.length) return

      const dirty = relevant.filter((section) => section.dirty())

      if (dirty.length) {

        raiseDesignerWarning(change, dirty.length === 1 ? dirty[0].label : (keys.includes('application') ? 'this application' : 'app settings'))
      }

      const clean = relevant.filter((section) => !section.dirty())

      if (!clean.length) return

      if (clean.length === 1 && clean[0].key === 'custom-themes') {

        await this.loadCustomThemes()

        return
      }

      const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
        headers: { 'application-unique-title': this.application }
      })

      for (const section of clean) {

        await section.apply(res.data)
      }
    },
    async loadSettings () {

      try {

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          headers: { 'application-unique-title': this.application }
        })

        if (res.data && res.data.theme) {

          this.savedTheme = res.data.theme
          this.previewTheme = res.data.theme
        }

        const zone = normalizeTimeZone(res.data && res.data.timeZone)
        this.timeZone = zone
        this.savedTimeZone = zone

        this.applyMail(res.data && res.data.mail)
        this.applyUserSession(res.data && res.data.userSession)
        this.applyAuthenticatorPolicy(res.data && res.data.authenticatorPolicy)
        this.applyPasswordPolicy(res.data && res.data.passwordPolicy)
        await this.applyRedirectHosts(res.data)
      } catch (err) {

        console.log('failed to load settings', err)
      }
    },
    async loadCustomThemes () {

      try {

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/custom-themes`, {
          headers: { 'application-unique-title': this.application }
        })

        this.customThemes = res.data || []
      } catch (err) {

        console.log('failed to load custom themes', err)
      }
    },
    triggerFileUpload () {

      this.$refs.fileInput.click()
    },
    readFileAsText (file) {

      return new Promise((resolve, reject) => {

        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsText(file)
      })
    },
    async handleFileUpload (event) {

      const file = event.target.files[0]

      if (!file) return

      event.target.value = ''

      if (!file.name.endsWith('.css')) {

        alert('Please select a .css file')
        return
      }

      if (file.size > 2 * 1024 * 1024) {

        alert('CSS file is too large (max 2MB)')
        return
      }

      const cssContent = await this.readFileAsText(file)

      let name = file.name.replace(/\.min\.css$/, '').replace(/\.css$/, '')
      const userNameInput = prompt('Theme name:', name)

      if (!userNameInput) return

      name = userNameInput
      this.uploading = true

      try {

        const res = await axios.post(
          `${VUE_APP_PROXY_SERVER_URL}/custom-themes`,
          { name, css: cssContent },
          { headers: { 'application-unique-title': this.application } }
        )

        this.customThemes.push(res.data)
        this.customCssCache[res.data.themeId] = cssContent
        this.previewTheme = res.data.themeId
      } catch (err) {

        console.log('failed to upload theme', err)
        alert('Failed to upload theme')
      } finally {

        this.uploading = false
      }
    },
    async deleteCustomTheme (themeId) {

      if (!confirm('Delete this custom theme?')) return

      try {

        await axios.delete(
          `${VUE_APP_PROXY_SERVER_URL}/custom-themes/${themeId}`,
          { headers: { 'application-unique-title': this.application } }
        )

        this.customThemes = this.customThemes.filter(t => t.themeId !== themeId)
        delete this.customCssCache[themeId]

        if (this.previewTheme === themeId) {

          this.previewTheme = 'default'
        }

        if (this.savedTheme === themeId) {

          this.savedTheme = 'default'

          await axios.put(
            `${VUE_APP_PROXY_SERVER_URL}/settings`,
            { theme: 'default' },
            { headers: { 'application-unique-title': this.application } }
          )
        }
      } catch (err) {

        console.log('failed to delete theme', err)
        alert('Failed to delete theme')
      }
    },
    async saveTheme () {

      if (!this.hasChanges) return

      this.saving = true
      this.saved = false

      try {

        await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, { theme: this.previewTheme }, {
          headers: { 'application-unique-title': this.application }
        })

        this.savedTheme = this.previewTheme
        this.saved = true

        setTimeout(() => { this.saved = false }, 2000)
      } catch (err) {

        console.log('failed to save settings', err)
      } finally {

        this.saving = false
      }
    },
    async applyRedirectHosts (settings) {

      const stored = settings && Object.prototype.hasOwnProperty.call(settings, 'redirectHostAllowList')

      this.hostsError = ''
      this.hostsSaved = false

      if (stored) {

        this.hostsFromPages = false
        this.redirectHostsText = redirectHostAllowList(settings).join('\n')
        this.rememberHosts()

        return
      }

      this.hostsFromPages = false
      this.redirectHostsText = ''

      if (!this.application) {

        this.rememberHosts()

        return
      }

      try {

        const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
          headers: { 'application-unique-title': this.application }
        })
        const hosts = redirectHostsFromPages(Array.isArray(res.data) ? res.data : [])

        this.redirectHostsText = hosts.join('\n')
        this.hostsFromPages = hosts.length > 0
      } catch (err) {

        this.hostsError = 'The pages could not be loaded, so hosts saved on a page are not shown yet.'
      }

      this.rememberHosts()
    },
    async saveRedirectHosts () {

      this.savingHosts = true
      this.hostsSaved = false
      this.hostsError = ''

      try {

        const redirectHosts = redirectHostAllowList({ redirectHostAllowList: this.redirectHostsText })
        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          redirectHostAllowList: redirectHosts
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.redirectHostsText = redirectHostAllowList(res.data || { redirectHostAllowList: redirectHosts }).join('\n')
        this.hostsFromPages = false
        this.rememberHosts()
        this.hostsSaved = true
        setTimeout(() => { this.hostsSaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.hostsError = (typeof data === 'string' && data) || 'Could not save the host list.'
      } finally {

        this.savingHosts = false
      }
    },
    applyAuthenticatorPolicy (value) {

      const policy = authenticatorPolicyForRead(value)

      this.authenticatorPolicy = policy
      this.savedAuthenticatorPolicy = policy
    },
    async saveAuthenticatorPolicy () {

      this.savingPolicy = true
      this.policySaved = false
      this.policyError = ''

      try {

        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          authenticatorPolicy: this.authenticatorPolicy
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.applyAuthenticatorPolicy(res.data && res.data.authenticatorPolicy)
        this.policySaved = true
        setTimeout(() => { this.policySaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.policyError = (typeof data === 'string' && data) || 'Could not save the authenticator policy.'
      } finally {

        this.savingPolicy = false
      }
    },
    applyUserSession (value) {

      const policy = userSessionForRead(value)
      const session = minutesToParts(policy.sessionMinutes)

      this.sessionCount = session.count
      this.sessionUnit = session.unit
      this.inactivityEnabled = policy.inactivityMinutes != null

      if (policy.inactivityMinutes) {

        const idle = minutesToParts(policy.inactivityMinutes)

        this.inactivityCount = idle.count
        this.inactivityUnit = idle.unit
      }

      const remembered = minutesToParts(displayedRememberMinutes(policy))

      this.rememberCount = remembered.count
      this.rememberUnit = remembered.unit
      this._sessionSnap = this.usersSessionSnap()
    },
    userSessionPayload () {

      const session = partsToMinutes(this.sessionCount, this.sessionUnit)

      if (session.error) return session

      let inactivityMinutes = null

      if (this.inactivityEnabled) {

        const idle = partsToMinutes(this.inactivityCount, this.inactivityUnit)

        if (idle.error) return idle

        inactivityMinutes = idle.minutes
      }

      const remembered = partsToMinutes(this.rememberCount, this.rememberUnit)

      if (remembered.error) return remembered

      const rememberMeMinutes = remembered.minutes < session.minutes ? session.minutes : remembered.minutes

      return normalizeUserSession({
        sessionMinutes: session.minutes,
        inactivityMinutes,
        rememberMeMinutes
      })
    },
    async saveUserSession () {

      this.savingSession = true
      this.sessionSaved = false
      this.sessionError = ''

      const payload = this.userSessionPayload()

      if (payload.error) {

        this.sessionError = payload.error
        this.savingSession = false

        return
      }

      try {

        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          userSession: {
            sessionMinutes: payload.sessionMinutes,
            inactivityMinutes: payload.inactivityMinutes,
            rememberMeMinutes: payload.rememberMeMinutes
          }
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.applyUserSession(res.data && res.data.userSession)
        this.sessionSaved = true
        setTimeout(() => { this.sessionSaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.sessionError = (typeof data === 'string' && data) || 'Could not save the sign-in session.'
      } finally {

        this.savingSession = false
      }
    },
    applyPasswordPolicy (value) {

      const policy = passwordPolicyForRead(value)

      this.minLength = policy.minLength == null ? '' : policy.minLength
      this.blockCommon = policy.blockCommon
      this.requireNumber = policy.requireNumber
      this.requireSymbol = policy.requireSymbol
      this.requireMixedCase = policy.requireMixedCase
      this.expiryDays = policy.expiryDays == null ? '' : policy.expiryDays
      this.preventReuse = policy.preventReuse
      this.lockoutMinutes = policy.lockoutMinutes
      this.lockoutMessage = policy.lockoutMessage
      this.clearAuthenticatorOnReset = policy.clearAuthenticatorOnReset
      this._passwordSnap = this.passwordSnap()
    },
    async savePasswordPolicy () {

      this.savingPasswords = true
      this.passwordsSaved = false
      this.passwordError = ''

      try {

        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          passwordPolicy: {
            minLength: this.minLength === '' ? null : this.minLength,
            blockCommon: this.blockCommon,
            requireNumber: this.requireNumber,
            requireSymbol: this.requireSymbol,
            requireMixedCase: this.requireMixedCase,
            expiryDays: this.expiryDays === '' ? null : this.expiryDays,
            preventReuse: this.preventReuse,
            lockoutMinutes: this.lockoutMinutes,
            lockoutMessage: this.lockoutMessage,
            clearAuthenticatorOnReset: this.clearAuthenticatorOnReset
          }
        }, {
          headers: { 'application-unique-title': this.application }
        })

        this.applyPasswordPolicy(res.data && res.data.passwordPolicy)
        this.passwordsSaved = true
        setTimeout(() => { this.passwordsSaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.passwordError = (typeof data === 'string' && data) || 'Could not save the password rules.'
      } finally {

        this.savingPasswords = false
      }
    },
    async saveTimeZone () {

      this.savingTimeZone = true
      this.timeZoneSaved = false
      this.timeZoneError = ''

      try {

        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, { timeZone: this.timeZone }, {
          headers: { 'application-unique-title': this.application }
        })

        this.savedTimeZone = normalizeTimeZone(res.data && res.data.timeZone)
        this.timeZone = this.savedTimeZone
        this.timeZoneSaved = true
        setTimeout(() => { this.timeZoneSaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.timeZoneError = (typeof data === 'string' && data) || 'Could not save the time zone.'
      } finally {

        this.savingTimeZone = false
      }
    },
    applyMail (mail) {

      // Keep a mailbox the visitor already started typing before settings finished loading.
      if (this._mailSnap == null && this.mail.accounts.length) return

      const normalized = normalizeMailSettings(mail || { accounts: [] })
      const value = normalized.mail || { accounts: [], defaultAccountId: '' }

      this.mail = {
        defaultAccountId: value.defaultAccountId,
        accounts: value.accounts.map(account => Object.assign({}, account))
      }
      this._mailSnap = JSON.stringify(this.mail)
    },
    addMailAccount () {

      const account = blankMailAccount()

      if (!this.mail.defaultAccountId) this.mail.defaultAccountId = account.id

      this.mail.accounts.push(account)
    },
    removeMailAccount (account) {

      this.mail.accounts = this.mail.accounts.filter(item => item.id !== account.id)

      if (this.mail.defaultAccountId === account.id) {

        this.mail.defaultAccountId = this.mail.accounts.length ? this.mail.accounts[0].id : ''
      }
    },
    makeDefaultMailAccount (account) {

      this.mail.defaultAccountId = account.id
    },
    setMailMode (account, mode) {

      const leaving = mailPortForMode(account.mode)
      const current = Number(account.port)

      account.mode = mode

      if (!current || current === leaving) account.port = mailPortForMode(mode)
    },
    async saveMail () {

      this.savingMail = true
      this.mailSaved = false
      this.mailError = ''

      try {

        const res = await axios.put(`${VUE_APP_PROXY_SERVER_URL}/settings`, { mail: this.mail }, {
          headers: { 'application-unique-title': this.application }
        })

        this.applyMail(res.data && res.data.mail)
        this.mailSaved = true
        setTimeout(() => { this.mailSaved = false }, 2000)
      } catch (err) {

        const data = err && err.response && err.response.data
        this.mailError = (typeof data === 'string' && data) || 'Could not save email settings.'
      } finally {

        this.savingMail = false
      }
    }
  }
}
</script>

<style scoped>

.settings-root {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.settings-panels {
  flex: 1;
  min-height: 0;
  margin-top: 0;
}

/* ── Left panel ── */
.theme-list-col {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 102px);
  border-right: 1px solid #2a2a27;
  padding: 0 !important;
}

.theme-list-header {
  padding: 14px 14px 8px;
  flex-shrink: 0;
}

.settings-section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #e8e8e6;
  margin-bottom: 2px;
}

.settings-section-desc {
  font-size: 0.75rem;
  color: #707070;
}

.dates-panel {
  padding: 1.5rem 1rem;
}

.dates-zone-select {
  max-width: 28rem;
  margin: 0.75rem 0;
}

.dates-error {
  color: #f0a0a0;
  font-size: 0.85rem;
}

.mail-panel {
  padding: 1.25rem 1.25rem 2rem;
  max-width: 46rem;
}

.mail-card {
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 12px;
  overflow: hidden;
}

.mail-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.05rem 1.2rem 1rem;
  border-bottom: 1px solid #2a2a27;
  background: linear-gradient(180deg, #1a1a17 0%, #141412 100%);
}

.mail-head-mark {
  flex-shrink: 0;
  width: 2.1rem;
  height: 2.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(154, 209, 43, 0.12);
  color: #9ad12b;
  font-size: 0.85rem;
}

.mail-head-copy {
  flex: 1;
  min-width: 0;
}

.mail-lead {
  margin: 0.15rem 0 0;
  line-height: 1.45;
}

.mail-empty {
  padding: 1.4rem 1.2rem 1.2rem;
  color: #8a8a88;
  font-size: 0.8rem;
}

.mail-empty p {
  margin: 0 0 0.8rem;
}

.mail-add-row {
  padding: 0.85rem 1.2rem;
  border-bottom: 1px solid #2a2a27;
}

.mail-add {
  border: 1px dashed #3a3a37;
  background: transparent;
  color: #b0b0ae;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.78rem;
  cursor: pointer;
}

.mail-add:hover {
  border-color: #9ad12b;
  color: #e8e8e6;
}

.mail-account {
  padding: 1rem 1.2rem 1.1rem;
  border-bottom: 1px solid #2a2a27;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.mail-account-head {
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
}

.mail-field-name {
  flex: 1;
  min-width: 0;
}

.mail-default,
.mail-default-set,
.mail-remove {
  flex-shrink: 0;
  border-radius: 999px;
  font-size: 0.72rem;
  padding: 0.28rem 0.65rem;
  margin-bottom: 0.15rem;
}

.mail-default {
  border: 1px solid rgba(154, 209, 43, 0.4);
  background: rgba(154, 209, 43, 0.12);
  color: #c6ee7a;
}

.mail-default-set,
.mail-remove {
  border: 1px solid #2a2a27;
  background: #1e1e1b;
  color: #b0b0ae;
  cursor: pointer;
}

.mail-default-set:hover,
.mail-remove:hover {
  border-color: #3e3e3c;
  color: #e8e8e6;
}

.mail-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.mail-mode {
  text-align: left;
  border: 1px solid #2a2a27;
  background: #1a1a17;
  color: #c0c0be;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.mail-mode.is-on {
  border-color: rgba(154, 209, 43, 0.55);
  background: rgba(154, 209, 43, 0.08);
}

.mail-mode-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #e8e8e6;
}

.mail-mode-detail {
  font-size: 0.7rem;
  color: #707070;
  line-height: 1.35;
}

.mail-pill {
  flex-shrink: 0;
  margin-top: 0.15rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  border: 1px solid #2a2a27;
}

.mail-pill-ready {
  color: #c6ee7a;
  background: rgba(154, 209, 43, 0.12);
  border-color: rgba(154, 209, 43, 0.4);
}

.mail-pill-empty {
  color: #8a8a88;
  background: #1e1e1b;
}

.mail-block {
  padding: 0.95rem 1.2rem 1.05rem;
  border-bottom: 1px solid #2a2a27;
}

.mail-block-title {
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #707070;
  margin-bottom: 0.65rem;
}

.mail-pair {
  display: grid;
  gap: 0.75rem;
}

.mail-pair-server {
  grid-template-columns: minmax(0, 1fr) 7.25rem;
}

.mail-pair-account {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.mail-field {
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  margin: 0;
  min-width: 0;
}

.mail-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #c0c0be;
}

.mail-tls {
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  margin: 0.8rem 0 0;
  padding: 0.7rem 0.8rem;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  background: #1a1a17;
  cursor: pointer;
}

.mail-tls input {
  margin-top: 0.18rem;
  accent-color: #9ad12b;
  width: 0.95rem;
  height: 0.95rem;
  flex-shrink: 0;
}

.mail-tls-copy {
  display: flex;
  flex-direction: column;
  gap: 0.12rem;
}

.mail-tls-name {
  font-size: 0.8rem;
  color: #e8e8e6;
  font-weight: 500;
}

.mail-tls-hint,
.mail-note,
.mail-actions-note {
  font-size: 0.72rem;
  color: #707070;
  line-height: 1.4;
}

.mail-note {
  margin: 0.55rem 0 0;
}

.mail-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1.2rem;
  background: #10100e;
}

.mail-actions-note {
  flex: 1;
  min-width: 0;
}

.mail-error {
  margin: 0;
  flex: 1;
  min-width: 0;
}

.mail-save {
  flex: 0 0 auto;
  margin-left: auto;
  min-width: 8.75rem;
  padding: 0.5rem 1.15rem;
  border: none;
  border-radius: 6px;
  background-color: #9ad12b;
  color: #10140a;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.12s;
}

.mail-save:hover {
  background-color: #b0e840;
}

.mail-save:disabled {
  opacity: 0.55;
  cursor: default;
}

@media (max-width: 640px) {
  .mail-head {
    flex-wrap: wrap;
  }

  .mail-pair-server,
  .mail-pair-account,
  .mail-modes {
    grid-template-columns: 1fr;
  }

  .mail-account-head {
    flex-wrap: wrap;
  }

  .mail-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .mail-save {
    width: 100%;
  }
}

.session-label {
  display: block;
  margin-top: 0.9rem;
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  color: #c0c0be;
}

.session-row {
  display: flex;
  gap: 0.5rem;
  max-width: 28rem;
}

.session-count {
  max-width: 8rem;
}

.session-unit {
  max-width: 10rem;
}

.session-note {
  margin-top: 0.9rem;
}

.session-save {
  max-width: 28rem;
  margin-top: 0.75rem;
}

.embed-hosts {
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #2a2a27;
  max-width: 36rem;
}

.embed-hosts textarea {
  margin-top: 0.35rem;
}

.policy-title {
  margin-top: 1.75rem;
}

.policy-choice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.55rem 0 0;
  color: #e8e8e6;
  font-size: 0.85rem;
}

.upload-btn {
  width: 100%;
  padding: 6px 0;
  margin-top: 8px;
  border-radius: 5px;
  border: 1px dashed #3a3a37;
  background: transparent;
  color: #707070;
  font-size: 0.75rem;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.upload-btn:hover {
  border-color: #9ad12b;
  color: #a0a09e;
}

.upload-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.theme-list {
  flex: 1;
  overflow-y: auto;
  padding: 6px 10px;
}

.theme-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 5px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
  margin-bottom: 2px;
}

.theme-card:hover {
  background-color: #1e1e1b;
}

.theme-card-previewing {
  border-color: #9ad12b !important;
  background-color: #1a1a17 !important;
}

.theme-card-saved {
  border-color: #2a2a27;
  background-color: #141412;
}

.theme-card-name {
  font-size: 0.8rem;
  color: #a0a09e;
  display: flex;
  align-items: center;
  gap: 6px;
}

.theme-card-previewing .theme-card-name {
  color: #e8e8e6;
}

.theme-card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.theme-card-custom-badge {
  font-size: 0.58rem;
  color: #9ad12b;
  background: rgba(154, 209, 43, 0.1);
  padding: 1px 5px;
  border-radius: 3px;
}

.theme-card-badge {
  font-size: 0.62rem;
  color: #707070;
  background: #1e1e1b;
  border: 1px solid #2a2a27;
  padding: 1px 6px;
  border-radius: 3px;
}

.theme-card-check {
  color: #9ad12b;
  font-size: 0.72rem;
}

.theme-delete-btn {
  background: transparent;
  border: none;
  color: #a8a8a4;
  font-size: 0.68rem;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: color 0.12s, background 0.12s;
}

.theme-delete-btn:hover {
  color: #e05050;
  background: rgba(224, 80, 80, 0.1);
}

.theme-list-footer {
  flex-shrink: 0;
  padding: 10px 14px;
  border-top: 1px solid #2a2a27;
}

.save-btn {
  width: 100%;
  padding: 7px 0;
  border-radius: 5px;
  border: none;
  background-color: #9ad12b;
  color: #0f0f0d;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s, background 0.12s;
}

.save-btn:hover {
  background-color: #b0e840;
}

.save-btn-disabled {
  opacity: 0.35;
  cursor: default;
  background-color: #3a3a37;
  color: #707070;
}

.save-btn-disabled:hover {
  background-color: #3a3a37;
}

/* ── Right panel ── */
.preview-col {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 102px);
  padding: 0 !important;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid #2a2a27;
}

.preview-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #c0c0be;
}

.preview-unsaved {
  font-size: 0.68rem;
  color: #e8a020;
  background: rgba(232, 160, 32, 0.1);
  padding: 1px 7px;
  border-radius: 3px;
}

.preview-frame-wrapper {
  flex: 1;
  min-height: 0;
  background: #1a1a17;
}

.preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}
</style>
